import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import LoadingScreen from "react-loading-screen";
import Header from "../component/headerone/Main";
import Breadcrumb from "../component/Breadcrumb/Main";
import Error from "../component/Pages/error/Main";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const FACULTY_IMAGE_BASE_URL =
  process.env.REACT_APP_FACULTY_IMAGE_BASE_URL ||
  "https://files.iittp.ac.in/images/faculty/";

const departmentRoutes = {
  "CHEMICAL ENGINEERING": "/people/faculty/chemical-engineering-department",
  CHEMISTRY: "/people/faculty/chemistry-department",
  "CIVIL ENGINEERING": "/people/faculty/civilengineering-department",
  "CIVIL & ENVIRONMENTAL ENGINEERING": "/people/faculty/civilengineering-department",
  "COMPUTER SCIENCE & ENGINEERING": "/people/faculty/computer-science-engineering-department",
  "COMPUTER SCIENCE ENGINEERING": "/people/faculty/computer-science-engineering-department",
  "ELECTRICAL ENGINEERING": "/people/faculty/electrical-engineering-department",
  "HUMANITIES AND SOCIAL SCIENCES": "/people/faculty/humanities-and-social-sciences-department",
  MATHEMATICS: "/people/faculty/mathematics-department",
  "MATHEMATICS & STATISTICS": "/people/faculty/mathematics-department",
  "MATHEMATICS AND STATISTICS": "/people/faculty/mathematics-department",
  MECHANICAL: "/people/faculty/mechanical-engineering-department",
  "MECHANICAL ENGINEERING": "/people/faculty/mechanical-engineering-department",
  PHYSICS: "/people/faculty/physics-department",
};

function normalizeUrl(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `https://${url}`;
}

function getFacultyImageUrl(imageName) {
  if (!imageName) return "";
  return imageName.startsWith("http") ? imageName : `${FACULTY_IMAGE_BASE_URL}${imageName}`;
}

function isSectionTitle(section, title) {
  return section?.section_title?.trim().toLowerCase() === title.toLowerCase();
}

function InfoRow({ icon, children }) {
  if (!children) return null;

  return (
    <p className="faculty-profile-info-row">
      {icon && <i className={`${icon} me-2`} />}
      {children}
    </p>
  );
}

function BulletList({ items }) {
  const cleanItems = items?.filter(Boolean) || [];

  if (!cleanItems.length) return null;

  return (
    <ul className="faculty-profile-list">
      {cleanItems.map((item, index) => (
        <li key={`${item}-${index}`}>
          <i className="fal fa-arrow-alt-circle-right me-3 c-link" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function HtmlSection({ title, html }) {
  if (!html?.trim()) return null;

  return (
    <div className="faculty-profile-section">
      <h3 className="c-headline">{title}</h3>
      <div
        className="faculty-rich-text"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
      />
    </div>
  );
}

export default function FacultyProfile({ subpage }) {
  const { slug, subpage: paramSubpage } = useParams();
  const actualSlug = subpage || slug || paramSubpage;
  const [facultyData, setFacultyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!actualSlug) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    async function fetchFacultyProfile() {
      setLoading(true);
      setNotFound(false);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/faculty/${encodeURIComponent(actualSlug)}`,
          { signal: controller.signal }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Faculty profile not found");
        }

        setFacultyData(result);
      } catch (error) {
        if (error.name === "AbortError") return;

        console.error("Faculty profile fetch failed:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchFacultyProfile();

    return () => controller.abort();
  }, [actualSlug]);

  const profile = facultyData?.profile || {};
  const links = useMemo(() => facultyData?.links || [], [facultyData]);
  const sections = useMemo(() => facultyData?.sections || [], [facultyData]);
  const imageUrl = getFacultyImageUrl(profile.main_img);
  const departmentRoute = departmentRoutes[profile.department?.trim().toUpperCase()];

  const displayedDesignation = useMemo(() => {
    return profile.designation || sections[0]?.section_title || "Faculty";
  }, [profile.designation, sections]);

  const customSections = useMemo(() => {
    return sections.filter(
      (section) =>
        !isSectionTitle(section, "Areas of Interest") &&
        !isSectionTitle(section, "Education") &&
        !isSectionTitle(section, "Latest Publications")
    );
  }, [sections]);

  const websiteLinks = useMemo(() => {
    const personalUrl = normalizeUrl(profile.personal_weblink);
    return links.filter((link) => normalizeUrl(link.url) !== personalUrl);
  }, [links, profile.personal_weblink]);

  return (
    <LoadingScreen
      loading={loading}
      bgColor="#f4f4f4"
      spinnerColor="#007bff"
      textColor="#555"
      text="IITT Faculty Profile Loading, please wait..."
    >
      {notFound && <Error />}
      {facultyData && (
        <>
          <Header />
          <Breadcrumb title="Home" subtitle="Faculty Profile" />
          <section className="faculty-profile-page team-area fix p-relative pt-90 pb-80">
            <div className="container">
              <div className="faculty-profile-shell">
                <div className="row gx-5 gy-4 align-items-start">
                  <div className="col-lg-4 col-md-12 col-sm-12">
                    <aside className="faculty-profile-card">
                      <div className="faculty-profile-photo">
                      {imageUrl ? (
                        <img src={imageUrl} alt={profile.name || "Faculty"} />
                      ) : (
                        <div className="faculty-profile-photo-placeholder">Photo not available</div>
                      )}
                      </div>

                      <div className="faculty-profile-card-body">
                        <h3>{profile.name}</h3>
                        <p className="faculty-profile-role">{displayedDesignation}</p>

                        <div className="faculty-profile-contact">
                          <InfoRow icon="fal fa-phone">{profile.phone}</InfoRow>
                          <InfoRow icon="fal fa-envelope">{profile.email}</InfoRow>
                          {profile.personal_weblink && (
                            <InfoRow icon="fal fa-globe">
                              <a
                                href={normalizeUrl(profile.personal_weblink)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Personal Website
                              </a>
                            </InfoRow>
                          )}
                          {websiteLinks.map((link, index) => (
                            <InfoRow key={`${link.title}-${index}`} icon="fal fa-link">
                              <a
                                href={normalizeUrl(link.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {link.title || "Website"}
                              </a>
                            </InfoRow>
                          ))}
                        </div>
                      </div>
                    </aside>
                  </div>

                  <div className="text-column col-lg-8 col-md-12 col-sm-12">
                    <div
                      className="faculty-profile-content s-about-content wow fadeInRight"
                      data-animation="fadeInRight"
                      data-delay=".2s"
                    >
                      <div className="faculty-profile-header">
                        <span className="faculty-profile-label">Faculty Profile</span>
                        <h2>{profile.name}</h2>
                        <p>{displayedDesignation}</p>
                      </div>

                      <div className="faculty-profile-department">
                        <strong>Department</strong>
                        <p>
                          {departmentRoute ? (
                            <Link to={departmentRoute}>{profile.department}</Link>
                          ) : (
                            profile.department
                          )}
                        </p>
                      </div>

                      {profile.about && (
                        <div className="faculty-profile-section faculty-profile-about">
                          <h3 className="c-headline">Brief Bio</h3>
                          <p>{profile.about}</p>
                        </div>
                      )}

                      <div className="text-dark">
                        

                        {!!facultyData.areas?.length && (
                          <div className="faculty-profile-section">
                            <h3 className="c-headline">Areas of Interest</h3>
                            <BulletList items={facultyData.areas} />
                          </div>
                        )}

                        {!!facultyData.education?.length && (
                          <div className="faculty-profile-section">
                            <h3 className="c-headline">Education</h3>
                            <BulletList items={facultyData.education} />
                          </div>
                        )}

                        <HtmlSection
                          title="Latest Publications"
                          html={facultyData.publications_html}
                        />

                        {customSections.map((section, index) => (
                          <HtmlSection
                            key={`${section.section_title}-${index}`}
                            title={section.section_title}
                            html={section.section_html}
                          />
                        ))}

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </LoadingScreen>
  );
}
